import { useEffect, useSyncExternalStore } from "react"
import dynamic from "next/dynamic"

import { clearActionRecords } from "@/lib/perf/perf-actions"
import { perfOptIn, resolvePerfReport } from "@/lib/perf/perf-setup"
import { selectSessionStatus } from "@/lib/storage/auth-slice"
import { useAppSelector } from "@/lib/storage/hooks"
import { selectIsMindtripUser } from "@/lib/storage/user-slice"

const PerfReportWidget = dynamic(
  () =>
    import("@/components/perf-report/perf-report-widget").then(
      (m) => m.PerfReportWidget
    ),
  { ssr: false }
)

// the opt-in is a client-only fact, so the server renders nothing
const noSubscription = () => () => {}
const noOptIn = () => false

// The slow-things report is a Mindtrip-only tool. The debug bar's Performance
// switch arms a tab, and the _document setup script reads that at load
// (lib/perf/perf-setup.ts); this is where the arming is checked against the
// signed-in user, and /api/perf-report checks the session again on the server
// before resolving anything against the build's source maps.
export function PerfReportLauncher() {
  const optIn = useSyncExternalStore(noSubscription, perfOptIn, noOptIn)
  const isMindtripUser = useAppSelector(selectIsMindtripUser)
  // the opt-in outlives a page load, so waiting for a settled session is what
  // keeps a Mindtrip user's own opt-in from being cleared while it loads
  const sessionSettled = useAppSelector(selectSessionStatus) !== "loading"
  const report = optIn && sessionSettled && !!isMindtripUser

  useEffect(() => {
    if (!optIn || !sessionSettled) return
    if (!resolvePerfReport(!!isMindtripUser)) {
      // anything recorded while the session was still loading
      clearActionRecords()
      return
    }
    void import("@/lib/perf/perf-session")
      .then(({ startPerfSession }) =>
        startPerfSession(window.__mtPerf?.profiler)
      )
      .catch(() => {})
  }, [optIn, isMindtripUser, sessionSettled])

  return report ? <PerfReportWidget /> : null
}
